import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { WorkerActivityPanel } from "./AdminStatusPanels";

function workerError(error, action) {
  if (error?.code === "INVALID_ADMIN_RESPONSE") {
    return "The admin service returned invalid worker data. Refresh before saving.";
  }
  if (error?.status === 403) return "The request could not be verified. Refresh the page and try again.";
  if (error?.status === 400) return "The capacity was rejected. Enter a whole number from 1 to 8.";
  return `Worker ${action} could not be completed. Please try again.`;
}

export default function WorkerPoolPanel({ api, refreshVersion, onRefresh, onDirtyChange, onSavingChange }) {
  const [pool, setPool] = useState(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [conflict, setConflict] = useState(null);
  const [reloadRequired, setReloadRequired] = useState(false);
  const [health, setHealth] = useState(null);
  const [activityError, setActivityError] = useState("");
  const operationRef = useRef(0);
  const savingRef = useRef(false);
  const dirty = pool !== null && draft !== String(pool.concurrency);
  const capacity = Number(draft);
  const validCapacity = draft.trim() !== "" && Number.isInteger(capacity) && capacity >= 1 && capacity <= 8;

  useLayoutEffect(() => () => { operationRef.current += 1; }, []);

  useEffect(() => {
    const operation = ++operationRef.current;
    setLoading(true);
    setError("");
    setMessage("");
    api.getWorkerPool().then((value) => {
      if (operation !== operationRef.current) return;
      setPool(value);
      setDraft(String(value.concurrency));
      setConflict(null);
      setReloadRequired(false);
    }).catch((loadError) => {
      if (operation !== operationRef.current || loadError?.status === 401) return;
      setError(workerError(loadError, "capacity loading"));
      setReloadRequired(true);
    }).finally(() => {
      if (operation === operationRef.current) setLoading(false);
    });
    return () => { operationRef.current += 1; };
  }, [api, refreshVersion]);

  useEffect(() => {
    let active = true;
    let pending = false;
    const load = async () => {
      if (pending) return;
      pending = true;
      try {
        const value = await api.getHealth();
        if (active) { setHealth(value); setActivityError(""); }
      } catch (loadError) {
        if (active && loadError?.status !== 401) {
          setActivityError("Worker activity could not be refreshed. Displayed activity is last known.");
        }
      } finally { pending = false; }
    };
    load();
    const timer = setInterval(load, 15_000);
    return () => { active = false; clearInterval(timer); };
  }, [api, refreshVersion]);

  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    onSavingChange(saving);
    return () => onSavingChange(false);
  }, [saving, onSavingChange]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async () => {
    if (!pool || !dirty || !validCapacity || loading || savingRef.current || conflict || reloadRequired) return;
    savingRef.current = true;
    const operation = ++operationRef.current;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const value = await api.saveWorkerPool(capacity, pool.revision);
      if (operation !== operationRef.current) return;
      setPool(value);
      setDraft(String(value.concurrency));
      setMessage("Shared worker capacity saved. The queue uses this limit for new assignments.");
    } catch (saveError) {
      if (operation !== operationRef.current || saveError?.status === 401) return;
      if (saveError?.status === 409 && saveError.current) {
        setConflict(saveError.current);
      } else {
        setError(workerError(saveError, "capacity save"));
        if (saveError?.requiresFreshLoad) setReloadRequired(true);
      }
    } finally {
      if (operation === operationRef.current) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  };

  const resolveConflict = (keepDraft) => {
    if (!conflict || savingRef.current) return;
    setPool(conflict);
    if (!keepDraft) setDraft(String(conflict.concurrency));
    setConflict(null);
    setError("");
    setMessage(keepDraft ? "Your entered value is retained. Save to apply it to the reviewed revision." : "Latest server capacity loaded.");
  };

  return (
    <div className="adminStatusList">
      <form className="adminConfigurationForm" onSubmit={(event) => { event.preventDefault(); save(); }}>
        <p>Light, reference and detailed assessments share one queue and the same worker capacity.</p>
        {loading && <p role="status">Loading worker capacity…</p>}
        {error && <div className="adminAlert adminAlertError" role="alert">{error}</div>}
        {pool && <>
          <fieldset className="adminFieldset" disabled={loading || saving}>
            <legend>Shared assessment capacity</legend>
            <label className="adminFormField">
              <span>Maximum simultaneous assessments</span>
              <input type="number" min="1" max="8" step="1" required value={draft}
                aria-label="Maximum simultaneous assessments" aria-describedby="worker-capacity-hint"
                onChange={(event) => { setDraft(event.target.value); setMessage(""); }} />
              <small id="worker-capacity-hint">Total assessments running across all workers (1–8). Lowering the limit lets running work finish before new work starts.</small>
            </label>
            <dl className="adminSyncGrid">
              <div><dt>Saved capacity</dt><dd>{pool.concurrency}</dd></div>
              <div><dt>Revision</dt><dd>{pool.revision}</dd></div>
              <div><dt>Updated (UTC)</dt><dd>{new Date(pool.updatedAt).toLocaleString(undefined, { timeZone: "UTC" })}</dd></div>
            </dl>
            <p className="adminFieldHint">The queue applies this setting directly. Model settings, daily budgets, and App backend configuration are managed separately.</p>
          </fieldset>
          {conflict && <div className="adminAlert adminAlertWarning" role="alert">
            <p>Capacity changed on the server to {conflict.concurrency} at revision {conflict.revision}. Your entered value ({draft}) has been retained.</p>
            <div className="adminConflictActions">
              <button type="button" className="adminSecondaryButton" onClick={() => resolveConflict(false)}>Use server capacity</button>
              <button type="button" className="adminSecondaryButton" onClick={() => resolveConflict(true)}>Keep my value on revision {conflict.revision}</button>
            </div>
          </div>}
          {message && <p role="status">{message}</p>}
        </>}
        <div className="adminFormActions">
          <button type="button" className="adminSecondaryButton" disabled={loading || saving} onClick={onRefresh}>Refresh workers</button>
          <button type="submit" className="adminPrimaryButton"
            disabled={!pool || !dirty || !validCapacity || loading || saving || !!conflict || reloadRequired}>
            {saving ? "Saving…" : "Save worker capacity"}
          </button>
        </div>
      </form>
      {activityError && <div className="adminAlert adminAlertWarning" role="alert">{activityError}</div>}
      {health ? <WorkerActivityPanel activity={health.workerActivity} stale={!!activityError} />
        : <p role="status">{activityError ? "Worker activity is unavailable." : "Loading worker activity…"}</p>}
    </div>
  );
}
